import { AppRouteRecordRaw } from '../types';
import { PARTNER_TYPES } from '@/constants/partner-types';

const Body = () => import('@/components/body.vue');
const UserBookmarks = () => import('@/pages/user-bookmarks/index.vue');
const IntegrationAndSettingsOverview = () =>
  import('@/pages/integration-and-settings/overview/IntegrationAndSettingsOverview.vue');
const PartnerOnboarding = () =>
  import('@/pages/integration-and-settings/onboarding/PartnerOnboarding.vue');
const PartnerApiCredentialsManagement = () =>
  import('@/pages/integration-and-settings/api-credentials/PartnerApiCredentialsManagement.vue');
const PartnerUISettings = () =>
  import('@/pages/integration-and-settings/ui-settings/UISettings.vue');
const PartnerDataValidationManagement = () =>
  import('@/pages/integration-and-settings/data-validation/PartnerDataValidationManagement.vue');
const PartnerFraudRulesManagement = () =>
  import('@/pages/integration-and-settings/fraud-rules/PartnerFraudRulesManagement.vue');
const Sitemap = () => import('@/pages/sitemap/Sitemap.vue');

export const commonRoutes: AppRouteRecordRaw[] = [
  {
    path: '/bookmarks',
    component: Body,
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: '',
        name: 'userBookmarks',
        component: UserBookmarks,
        meta: {
          title: 'Bookmarks | ryd Portal',
          headTitle: 'Bookmarks',
          breadcrumb: 'Bookmarks',
        },
      },
    ],
  },
  // Backward-compat redirect: Bookmarks moved from /user-bookmarks.
  { path: '/user-bookmarks', redirect: '/bookmarks' },
  // The Alerts page itself lives under Platform Status & Alerts; /alerts is the
  // short URL the header bell icon navigates to.
  { path: '/alerts', redirect: '/platform-status-and-alerts/alerts' },
  {
    path: '/integration-and-settings',
    component: Body,
    meta: {
      title: 'Integration Settings | ryd Portal',
      breadcrumb: 'Integration Settings',
    },
    children: [
      {
        path: '',
        redirect: '/integration-and-settings/overview',
      },
      {
        path: 'overview',
        name: 'integrationAndSettingsOverview',
        component: IntegrationAndSettingsOverview,
        meta: {
          requiresAuth: true,
          requiredPartnerTypes: ['RYD_INTERNAL', 'API_PARTNER', 'FLEET_PARTNER'],
          breadcrumb: 'Overview',
        },
      },
      {
        path: 'onboarding',
        name: 'apiPartnerOnboarding',
        component: PartnerOnboarding,
        meta: {
          requiresAuth: true,
          // Admits API partners and fleet(-API) partners; the page itself
          // renders the API-partner or fleet-API variant based on useFleetApis.
          requiredPartnerTypes: [
            PARTNER_TYPES.API_PARTNER,
            PARTNER_TYPES.FLEET_PARTNER,
            PARTNER_TYPES.RYD_INTERNAL,
          ],
          title: 'Onboarding & Getting Started',
          breadcrumb: 'Onboarding & Getting Started',
        },
      },
      {
        path: 'api-credentials',
        name: 'apiCredentials',
        component: PartnerApiCredentialsManagement,
        meta: {
          requiresAuth: true,
          requiredRoles: ['api_credentials_admin'],
          requiredPartnerTypes: ['RYD_INTERNAL', 'API_PARTNER', 'FLEET_PARTNER'],
          breadcrumb: 'API Credentials',
        },
      },
      {
        path: 'ui-settings',
        name: 'PartnerUISettings',
        component: PartnerUISettings,
        meta: {
          title: 'UI Customization | ryd Portal',
          requiresAuth: true,
          requiredRoles: ['api_credentials_admin'],
          requiredPartnerTypes: ['RYD_INTERNAL', 'API_PARTNER', 'FLEET_PARTNER'],
          breadcrumb: 'UI Customization',
        },
      },
      {
        path: 'data-validation',
        name: 'partnerDataValidation',
        component: PartnerDataValidationManagement,
        meta: {
          requiresAuth: true,
          requiredRoles: ['api_credentials_admin'],
          requiredPartnerTypes: ['RYD_INTERNAL', 'API_PARTNER', 'FLEET_PARTNER'],
          breadcrumb: 'Partner Data Validation',
        },
      },
      {
        path: 'fraud-rules',
        name: 'partnerFraudRules',
        component: PartnerFraudRulesManagement,
        meta: {
          requiresAuth: true,
          requiredRoles: ['api_credentials_admin'],
          requiredPartnerTypes: ['RYD_INTERNAL', 'API_PARTNER', 'FLEET_PARTNER'],
          breadcrumb: 'Partner Fraud Rules',
        },
      },
    ],
  },
  // Backwards-compatible redirects.
  //
  // The former /partner/* routes (deployed before the migration to
  // Integration Settings) and the older /settings/* URLs referenced by
  // external documentation both point at their new homes under
  // /integration-and-settings/*. Keep these redirects in place so existing
  // bookmarks, docs, and inbound links keep working.
  { path: '/partner/api-credentials', redirect: '/integration-and-settings/api-credentials' },
  { path: '/partner/ui-settings', redirect: '/integration-and-settings/ui-settings' },
  { path: '/partner/data-validation', redirect: '/integration-and-settings/data-validation' },
  { path: '/partner/fraud-rules', redirect: '/integration-and-settings/fraud-rules' },
  { path: '/settings', redirect: '/integration-and-settings/overview' },
  { path: '/settings/api-credentials', redirect: '/integration-and-settings/api-credentials' },
  { path: '/settings/ui-settings', redirect: '/integration-and-settings/ui-settings' },
  { path: '/settings/ui-customization', redirect: '/integration-and-settings/ui-settings' },
  { path: '/settings/data-validation', redirect: '/integration-and-settings/data-validation' },
  { path: '/settings/fraud-rules', redirect: '/integration-and-settings/fraud-rules' },
  {
    path: '/sitemap',
    component: Body,
    meta: {
      breadcrumb: 'Portal Information',
      requiredPartnerTypes: [PARTNER_TYPES.RYD_INTERNAL, PARTNER_TYPES.API_PARTNER],
    },
    children: [
      {
        path: '',
        name: 'sitemap',
        component: Sitemap,
        meta: {
          title: 'Portal Sitemap | ryd Portal',
          headTitle: 'Portal Sitemap',
          breadcrumb: 'Sitemap',
        },
      },
    ],
  },

  // TODO: remove later
  // Redirects for backwards compatibility
  {
    path: '/pages/sitemap',
    redirect: '/sitemap',
  },
];
